import EditIcon from '@mui/icons-material/Edit'
import { Alert, Box, Button, Chip, Divider, Stack, Typography } from '@mui/material'
import { ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import { BRAND_CORAL, BRAND_CORAL_WASH } from '../../constants'
import { AddressBlock, PhoneValue, StageKey, VendorOnboardingForm } from '../../interfaces'
import { StageHeading } from '../StepChrome'

const showPhone = (phone?: PhoneValue) =>
    phone?.number ? `${phone.dial} ${phone.number}` : '—'

const Row = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
            gap: { xs: 0.25, sm: 2 },
            py: 0.75,
        }}
    >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
            {value || '—'}
        </Typography>
    </Box>
)

const Card = ({
    title,
    onEdit,
    children,
}: {
    title: string
    onEdit: () => void
    children: ReactNode
}) => (
    <Box
        sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: '#fff',
            p: 2.5,
        }}
    >
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
        >
            <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
            <Button
                size="small"
                startIcon={<EditIcon fontSize="small" />}
                onClick={onEdit}
                sx={{ textTransform: 'none', color: BRAND_CORAL }}
            >
                Edit
            </Button>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {children}
    </Box>
)

const addressLines = (a: AddressBlock) =>
    [
        a.attention && `Attn: ${a.attention}`,
        a.street1,
        a.street2,
        [a.city, a.state, a.pinCode].filter(Boolean).join(', '),
        a.country,
    ]
        .filter(Boolean)
        .join('\n')

interface Props {
    goTo: (stage: StageKey) => void
    submitError: string | null
}

const ReviewStep = ({ goTo, submitError }: Props) => {
    const { getValues } = useFormContext<VendorOnboardingForm>()
    const values = getValues()
    const shipping = values.sameAsBilling ? values.billing : values.shipping

    return (
        <Box>
            <StageHeading
                title="Review and submit"
                blurb="Check everything below, then submit. You can jump back to any section."
            />

            {submitError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {submitError}
                </Alert>
            )}

            <Stack spacing={2}>
                <Card title="Company" onEdit={() => goTo('company')}>
                    <Row label="Company name" value={values.companyName} />
                    <Row label="PAN number" value={values.panNumber} />
                    <Row label="Your email" value={values.submitterEmail} />
                </Card>

                <Card title="Agreement" onEdit={() => goTo('agreement')}>
                    <Row
                        label="GST certificate"
                        value={values.gstCertificate?.name ?? 'Not attached'}
                    />
                    <Row label="Signing authority" value={values.signingAuthorityName} />
                    <Row label="Signatory email" value={values.signingAuthorityEmail} />
                </Card>

                <Card title="Invoicing" onEdit={() => goTo('invoicing')}>
                    <Row label="GST number" value={values.gstNumber} />
                    <Row
                        label="Billing address"
                        value={
                            <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                                {addressLines(values.billing)}
                            </Box>
                        }
                    />
                    <Row label="Billing phone" value={showPhone(values.billing.phone)} />
                    <Row
                        label="Shipping address"
                        value={
                            values.sameAsBilling ? (
                                'Same as billing address'
                            ) : (
                                <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                                    {addressLines(shipping)}
                                </Box>
                            )
                        }
                    />
                </Card>

                <Card title={`Contacts (${values.contacts.length})`} onEdit={() => goTo('contacts')}>
                    {values.contacts.map((contact, index) => (
                        <Box key={index} sx={{ py: 1 }}>
                            {index > 0 && <Divider sx={{ mb: 1 }} />}
                            <Row
                                label={
                                    index === 0 ? (
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                            }}
                                        >
                                            Contact 1
                                            <Chip
                                                component="span"
                                                size="small"
                                                label="Primary"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    color: BRAND_CORAL,
                                                    backgroundColor: BRAND_CORAL_WASH,
                                                    border: '1px solid',
                                                    borderColor: BRAND_CORAL,
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        `Contact ${index + 1}`
                                    )
                                }
                                value={`${contact.firstName} ${contact.lastName}`.trim()}
                            />
                            <Row label="Email" value={contact.email} />
                            <Row label="Phone" value={showPhone(contact.phone)} />
                            <Row
                                label="Invoices"
                                value={contact.sendInvoice ? 'Yes — send invoices' : 'No'}
                            />
                        </Box>
                    ))}
                </Card>
            </Stack>
        </Box>
    )
}

export default ReviewStep
